import { isNil } from "lodash-es";
import type { FC } from "react";
import { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { twMerge } from "tailwind-merge";

import { NumberFieldWithStepperButtons } from "../../forms/NumberFieldWithStepperButtons";

import { GroupPageDropdown } from "components/footer/tools-menu/GroupPageDropdown";
import { ErrorMessageBanner } from "components/forms/ErrorMessageBanner";
import { BreakoutGroupKind } from "components/server-types";
import { t } from "i18n/i18n";
import { RadioButton } from "mds/components/RadioButton";
import { ToggleSwitch } from "mds/components/ToggleSwitch";
import { Tooltip } from "mds/components/Tooltip";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { ClockIcon } from "mds/icons";
import { storeApi, useAppSelector } from "store/index";
import {
  selectAssessmentsByPageId,
  selectCountStudentsInVirtualClass,
  selectGroupPagesForCurrentSection,
} from "store/selectors";
import { useListSelector } from "store/store-hooks";
import { BUTTON_EVENT_CATEGORY, trackEvent } from "utils/amplitude";
import {
  DEFAULT_BREAKOUT_DURATION,
  MAX_BREAKOUT_DURATION,
  MAX_GROUP_COUNT,
  MIN_BREAKOUT_DURATION,
  MIN_GROUP_COUNT,
  useBreakouts,
} from "utils/breakouts";

type FormValues = {
  breakout_group_type: BreakoutGroupKind;
  group_count: number;
  group_page_id: string | null;
  breakout_duration: number;
  should_sync_breakouts_with_page_groups: boolean;
};

type BreakoutGroupsConfigFormProps = {
  className?: string;
  onClose: () => void;
};

export const BreakoutGroupsConfigForm: FC<BreakoutGroupsConfigFormProps> = ({ onClose }) => {
  const pages = useListSelector(selectGroupPagesForCurrentSection);

  const { onStartBreakouts } = useBreakouts();

  const form = useForm<FormValues>({
    defaultValues: {
      breakout_group_type: pages.length > 0 ? "page" : "random",
      group_count: MIN_GROUP_COUNT,
      group_page_id: null,
      breakout_duration: DEFAULT_BREAKOUT_DURATION,
      should_sync_breakouts_with_page_groups: true,
    },
  });

  const { register, watch } = form;

  const shouldSyncBreakoutsWithPageGroups = watch("should_sync_breakouts_with_page_groups");
  const breakoutGroupType = watch("breakout_group_type");
  const groupPageId = watch("group_page_id");
  const groupCount = watch("group_count") ?? MIN_GROUP_COUNT;

  const isGroupPageBreakoutGroupType = breakoutGroupType === "page";
  const nStudents = useAppSelector(selectCountStudentsInVirtualClass);
  const groupPageAssessments =
    useAppSelector((s) => selectAssessmentsByPageId(s, groupPageId)) ?? [];
  const groupPageHasAssessments = groupPageAssessments.length > 0;

  const groupTypeRegistration = register("breakout_group_type", { required: true });
  register("group_page_id", {
    required: isGroupPageBreakoutGroupType ? t("error.field_required") : false,
  });

  const onSubmit = form.handleSubmit((data) => {
    const {
      breakout_group_type,
      group_count,
      group_page_id,
      breakout_duration,
      should_sync_breakouts_with_page_groups,
    } = data;

    onStartBreakouts(
      breakout_group_type,
      breakout_duration,
      group_page_id,
      group_count,
      groupPageHasAssessments ? false : should_sync_breakouts_with_page_groups,
    );

    onClose();
  });

  useEffect(() => {
    if (!groupPageId) return;
    storeApi.assessments.list({ submission__page_id: groupPageId });
  }, [groupPageId]);

  return (
    <FormProvider {...form}>
      <form className="mt-5 flex flex-col" onSubmit={onSubmit}>
        {pages.length === 0 ? (
          <div className="flex flex-col gap-1">
            <RadioButton
              containerClassName="items-center text-black-tint-55 text-sm font-medium"
              name="breakout_group_type"
              disabled
            >
              {t("breakout_group_modal.use_groups_from_page")}
            </RadioButton>

            <div className="mx-6 text-xs text-black-tint-55">
              {t("breakout_group_modal.no_group_pages")}
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-1">
            <RadioButton
              containerClassName="items-center text-black-tint-20 text-sm font-medium"
              id={t("breakout_group_modal.use_groups_from_page")}
              value="page"
              {...groupTypeRegistration}
              eventName="Breakouts Setup - Use existing group page"
            >
              {t("breakout_group_modal.use_groups_from_page")}
            </RadioButton>

            <div className="mx-6 mb-1 text-xs">
              {t("breakout_group_modal.use_groups_from_page_description")}
            </div>

            <GroupPageDropdown />
            <ErrorMessageBanner className="mx-6" name="group_page_id" />

            <div className="mx-6 mt-1 flex items-center gap-2">
              <Tooltip
                text={
                  groupPageHasAssessments
                    ? t("group_configuration_card.breakouts_synced_disabled_tooltip")
                    : undefined
                }
              >
                <ToggleSwitch
                  className="disabled:cursor-not-allowed"
                  disabled={!isGroupPageBreakoutGroupType || groupPageHasAssessments}
                  isSelected={groupPageHasAssessments ? false : shouldSyncBreakoutsWithPageGroups}
                  labelStart={
                    <div className="text-sm font-medium text-black-tint-20">
                      {t("breakout_group_modal.keep_groups_and_breakouts_in_sync")}
                    </div>
                  }
                  switchClassName={twMerge(
                    "data-[checked]:bg-blue-tint-20",
                    !isGroupPageBreakoutGroupType && "cursor-not-allowed",
                  )}
                  onClick={() =>
                    form.setValue(
                      "should_sync_breakouts_with_page_groups",
                      !shouldSyncBreakoutsWithPageGroups,
                    )
                  }
                />
              </Tooltip>
            </div>
          </div>
        )}

        <div className="mt-4 flex flex-col gap-1">
          <RadioButton
            containerClassName="items-center text-black-tint-20 text-sm font-medium"
            id={t("breakout_group_modal.assign_groups_randomly")}
            value="random"
            {...groupTypeRegistration}
            eventName="Breakouts Setup - Assign groups randomly"
            onClick={() => {
              form.clearErrors("group_page_id");
            }}
          >
            {t("breakout_group_modal.assign_groups_randomly")}
          </RadioButton>

          <div className="mx-6 mb-1 text-xs">
            {t("breakout_group_modal.assign_groups_randomly_description", {
              studentCount: t("breakout_group_modal.studentCount", {
                count: nStudents,
              }),
              groupCount: t("breakout_group_modal.groupCount", {
                count: groupCount,
              }),
              perGroup: t("breakout_group_modal.perGroup", {
                count: Math.ceil(nStudents / groupCount),
              }),
            })}
          </div>

          <div className="mx-6 mb-1 text-sm font-medium text-black-tint-20">
            {t("breakout_group_modal.number_of_breakout_groups")}
          </div>

          <NumberFieldWithStepperButtons
            className="mx-6"
            label={t("breakout_group_modal.groups")}
            max={MAX_GROUP_COUNT}
            min={MIN_GROUP_COUNT}
            {...form.register("group_count", {
              required: !isGroupPageBreakoutGroupType && t("error.field_required"),
              max: MAX_GROUP_COUNT,
              min: MIN_GROUP_COUNT,
              disabled: isGroupPageBreakoutGroupType,
              onChange: (e) => {
                const value = parseInt(e.target.value, 10);
                trackEvent("Breakouts Setup - Number of groups updated", {
                  ...BUTTON_EVENT_CATEGORY,
                  newGroupCount: value,
                });
              },
              valueAsNumber: true,
              validate: {
                isWholeNumber: (value) => {
                  if (!isGroupPageBreakoutGroupType && !Number.isInteger(value)) {
                    return t("error.whole_number_required");
                  }
                  return true;
                },
                withinRange: (value) => {
                  if (
                    !isGroupPageBreakoutGroupType &&
                    !isNil(value) &&
                    (value < MIN_GROUP_COUNT || value > MAX_GROUP_COUNT)
                  ) {
                    return t("error.group_count_out_of_range", {
                      min: MIN_GROUP_COUNT,
                      max: MAX_GROUP_COUNT,
                    });
                  }
                  return true;
                },
              },
            })}
          />
          <ErrorMessageBanner className="mx-6" name="group_count" />
        </div>

        <div className="my-8 flex flex-col gap-2">
          <div className="flex justify-between">
            <div className="flex items-center gap-2 text-sm font-medium text-black-tint-10">
              <ClockIcon />
              {t("breakout_group_modal.breakout_duration")}
            </div>

            <NumberFieldWithStepperButtons
              label={t("breakout_group_modal.min")}
              max={MAX_BREAKOUT_DURATION}
              min={MIN_BREAKOUT_DURATION}
              {...form.register("breakout_duration", {
                required: t("error.field_required"),
                max: MAX_BREAKOUT_DURATION,
                min: MIN_BREAKOUT_DURATION,
                validate: {
                  isWholeNumber: (value) => {
                    const number = Number(value);
                    if (!Number.isInteger(number)) {
                      return t("error.whole_number_required");
                    }
                    return true;
                  },
                  withinRange: (value) => {
                    const number = Number(value);
                    if (
                      !isNil(value) &&
                      (number < MIN_BREAKOUT_DURATION || number > MAX_BREAKOUT_DURATION)
                    ) {
                      return t("error.duration_out_of_range", {
                        min: MIN_BREAKOUT_DURATION,
                        max: MAX_BREAKOUT_DURATION,
                      });
                    }
                    return true;
                  },
                },
              })}
            />
          </div>
          <ErrorMessageBanner name="breakout_duration" />
        </div>

        <div className="flex flex-row justify-end gap-2">
          <SquareButton kind="tertiary" size="m" onClick={onClose}>
            {t("common.cancel")}
          </SquareButton>

          <SquareButton kind="accent" size="m" type="submit">
            {t("breakout_group_modal.start_breakouts")}
          </SquareButton>
        </div>
      </form>
    </FormProvider>
  );
};
